import * as array from 'd3-array'
import * as d3 from 'd3-selection'
import { nest } from 'd3-collection'
import * as shape from 'd3-shape'
import { scaleBand as scale } from 'd3-scale'

const hasXAxis = ['line', 'area', 'bar', 'polar'] // What chart types can show a x-axis

const hasYAxis = ['line', 'area', 'bar'] // Chart types using the default y-axis

// These serie types use a zero based stack offset
const zeroOffsetStackables = ['area', 'waffle', 'beaker', 'pyramid', 'funnel']

const ANALYTICS_SERIES = ['reference-band', 'regression']

/*
 * `reduce` function to group an array based on each
 * item in the array's `_columnIdx`
 */
const groupByColumn = (p, c) => {
  (p[c._columnIdx] = p[c._columnIdx] || []).push(c)
  return p
}

/*
 * Determines the breakdown to use. By default it looks at the incoming
 * `plot.breakdown`, but if the range is `empty`, it creates a range by inspecting
 * the series points, which is used for charts without a proper breakdown. e.g. single period bar chars
 */
const breakdown = function (plot) {
  let breakdown = plot.breakdown

  if (breakdown && breakdown.range && breakdown.range.length > 0) {
    // Easy. They delivered us the breakdown, no guessing
    return plot.breakdown
  }

  // We have to generate a fake breakdown for the bar series. For other chart types we don't
  // use a fake breakdown -- just bar charts.
  let values = Object.values(plot.series.filter(serie => serie.type === 'bar')
                                        .reduce(groupByColumn, {}))

  if (plot.series[0] && (plot.series[0].type === 'pie' || plot.series[0].type === 'donut')){
    // TODO This is a hack for fake breakdowns in pie/donuts.
    values = [...plot.series.map(serie => [serie])]
  }

  return {
    title: breakdown && breakdown.title,
    calculated: true, // Sets a flag to indicate the breakdown was calculated by chart code, and not sent to us
                      // Can be used to see if we're graphing multiple series with a single point.
    range: values.map((column, i) => {
      // Each column maps to a value in the range
      let id = `no-breakdown-${column.map(c => c.id).join('-')}`
      column.forEach(serie => {
        serie.points[0] = {...serie.points[0], rangeId: id} // Override the point[0] rangeId
        // Override the _columnIdx
        // This makes sure everything is aligned directly on the range center
        serie._columnIdx = 0
      })
      return {
        title: (column.length > 1 && column[0].stackBy) ? column[0].stackTitle : column[0].title, // If it's a stack use `stackTitle`, otherwise use the serie title
        subtitle: column[0].subtitle, // TODO stacks might have subtitles?
        id,
        serieOrder: column.map(c => c.id) // Store the order of the series in the stack
      }
    })
  }
}

class Layout {

  constructor (data) {
    this.axes = data.axes || {}
    this.plots = data.plots || []

    this.rtl = document.documentElement.dir === 'rtl'

    this._columnize({
      width: data.width,
      height: data.height
    }) // Builds up the column structure and creates the slots
  }

  maxValue (axis = 'left') {
    let slotMax = array.max(this._slots.filter(slot => (slot.axis || 'left') === axis), slot => slot[1])
    return slotMax || 0
  }

  minValue (axis = 'left') {
    let slotMin = array.min(this._slots.filter(slot => (slot.axis || 'left') === axis), slot => array.min(slot))
    return slotMin || 0
  }

  /*
   * Returns if this chart is hoverable. Only supported for certain serie types
   * This uses our default basic tooltip. Certain charts implement their own
   * hover support
   */
  hoverable () {
    return this.getTypes().some(type => ['line', 'bar', 'area'].includes(type))
  }

  /*
   * Returns an array of series types on the chart
   */
  getTypes () {
    return [...new Set(...this.plots.map(plot => plot.series.map(serie => serie.type)))]
  }

  /*
   * Returns the formatter that should be used for the given axis.
   * Uses `axes.overrides[axis].formatter` if defined. Then falls back
   * to the formatter attached to a serie on that axis. Then a no-op
   */
  getFormatter (axis) {
    let overrides = this.axes[axis] || {}
      , serie = this.plots.map(p => p.series).flat().find(s => s.axis === axis)

    if (!serie) {
      serie = {}
    }

    return overrides.formatter || serie.formatter || (d => d)
  }

  /*
   * Builds a grid layout based on the defined range of this chart. If the breakdown
   * is calculated, we mock a range.
   *
   * This grid layout can be used for non-cartesian style charts, such as pie, waffle, etc.
   * where we need to draw multiple repeated objects. e.g. If a pie is shown
   * with more than one range, we need to draw multiple pies.
   *
   * The aspect ratio defaults to 1:1.
   *
   * Returns an object containing the blocks of the grid, each block maps to
   * an item in the range.
   */
  getGrid (dimensions, aspect = 1, xPadding = .2, yPadding = .2, forcedColumnCount, forcedRowCount) {
    // Determine the range. If it's calcalulated, we fake the range so we show
    // a single pie
    let range = this.breakdown.calculated ? [{id: 'calculated-range'}] : this.breakdown.range
      , blockWidth = Math.sqrt(aspect * dimensions.width * dimensions.height / range.length)
      , columns = range.length === 1 ? 1 : (forcedColumnCount || (Math.floor(dimensions.width / blockWidth) || 1))
      , rows = forcedRowCount || Math.ceil(range.length / columns)
      , x = scale().domain(array.range(columns))
                   .range([0, dimensions.width])
                   .paddingInner(columns > 1 ? xPadding : 0)
      , y = scale().domain(array.range(rows))
                   .range([0, dimensions.height])
                   .paddingInner(rows > 1 ? yPadding : 0) // See #374
      , result = {
        blocks: [],
        width: x.bandwidth(),
        height: y.bandwidth()
      }

    for (let i = 0; i < range.length; i++) {
      let col = i % columns
        , row = Math.floor(i / columns)

      result.blocks.push({
        range: range[i],
        dimensions: {
          x: x(col),
          y: y(row),
          width: x.bandwidth(),
          height: y.bandwidth()
        }
      })
    }

    return result
  }

  yScaleOverrides (axis, minY, maxY) {
    let overrides = this.axes[axis] || {}

    // Determine overrides.
    // See https://github.com/SpiderStrategies/scoreboard-chart/issues/49
    if (typeof overrides.yMin !== 'undefined') {
      minY = Math.max(overrides.yMin, minY)
    }
    if (typeof overrides.yMax !== 'undefined') {
      maxY = Math.min(overrides.yMax, maxY)
    }

    // If yMinForce or yMaxForce set, then forcefully override the min/max
    if (typeof overrides.yMinForce !== 'undefined') {
      minY = overrides.yMinForce
    }
    if (typeof overrides.yMaxForce !== 'undefined') {
      maxY = overrides.yMaxForce
    }

    // Check if this axes is a stacked
    if (this._stackOffsets[axis].has(shape.stackOffsetExpand)) {
      // Force a 0, 1 y-scale
      return [0, 1]
    }

    return [minY, maxY]
  }

  /*
   * Returns the max number of columns a given plot/range will show
   *
   */
  columnCount () {
    return this.maxColumnsPerGroup || 0
  }

  /*
   * Adds the unstackable points to our slots.
   */
  _addSlot (point, serie) {
    let raw = point.raw || 0
      , min = 0

    if (point.raw && point.raw < min) {
      min = point.raw
    }

    if (serie.type === 'bar' && point.raw < 0) {
      // Make sure negative bars have substance
      raw = 0
      min = point.raw
    }

    /*
     * reference-band serie types don't have a `point.raw` since there
     * are thresholds defining the raw values. i.e. a single serie can show
     * multiple values at a particular point.rangeId
     */
    if (point.thresholds) {
      // Set the raw value to the max threshold
      let raws = point.thresholds.map(threshold => threshold.raw)
      raw = array.max(raws)
      min = array.min(raws)
    }

    if (point.range) {
      // Forecast data has a `range` for the outer cone. We want to include
      // that range in our y-axis values
      [min, raw] = array.extent(point.range)
    }

    point._range = [min, raw]
    point._range.axis = serie.axis
    if ((point && point.raw !== undefined) || (point && point.thresholds)) {
      // only add the slot if there's a value attached to the point
      let [low, high] = point._range

      if (serie.type === 'line' && !point.range) {
        // Line charts don't have a min value for the slot. They match the raw value. See Impact #40127
        // Also make sure it's not a forecast cone w/ the `range`
        low = high
      }

      let _slot = [low, high]
      _slot.axis = serie.axis
      this._slots.push(_slot)
    }
  }

  /*
   * Returns the non-analytics serie types from the list of serie
   */
  nonAnalyticsSeries (series) {
    return series.filter(serie => {
      return ANALYTICS_SERIES.indexOf(serie.type) === -1
    })
  }

  /*
   * Sets the _columnIdx of series. This is used when multiple series are broken into
   * columns within the same tick of the range. e.g. there can be multiple bars in a single
   * tick, such as one normal bar and one stacked bar.
   */
  _setColumnIndex (series) {
    let idx = -1
      , bars = this.nonAnalyticsSeries(series).filter(serie => serie.type === 'bar')

    series.forEach(serie => {
      // Set a default _columnIdx on all series. See https://github.com/SpiderStrategies/Scoreboard/issues/45861
      serie._columnIdx = 0
    })

    // Now override the _columnIdx for bars.

    // Ignore columnIdx on analytic style series
    bars.forEach((serie, i, series) => {
      if (!i || (!serie.stackBy || serie.stackBy !== series[i - 1].stackBy)) {
        // First one, bar that isn't stacked, or a bar with a new `stackBy`
        idx++ // new column
      }
      serie._columnIdx = idx
    })

    // See Impact #41469 -- Reverse the order in which we display the bar series
    // columns so they appear left to right. the way impact sends the data to match the UI listing,
    // the bars would appear RTL. we want LTR
    bars.forEach((serie, i, series) => {
      serie._columnIdx = idx - serie._columnIdx
    })
  }

  /*
   * Adds all the points in all the incoming `series` to our slots
   */
  _addUnstackables (series) {
    series.filter(serie => !this._isStackable(serie)).forEach(serie => {
      serie.points.forEach(point => {
        // Since it's the only serie in this column, it's both a top and bottom stack
        point._topStack = true
        point._bottomStack = true
        this._addSlot(point, serie)
      })

      if (serie.forecast) {
        serie.forecast.points.forEach(point => this._addSlot(point, serie))
      }

      if (hasYAxis.indexOf(serie.type) !== -1) {
        this._stackOffsets[serie.axis || 'left'].add(null)
      }
    })
  }

  _isStackable (serie) {
    return serie.stackBy ||
      serie.type === 'waffle' ||
      serie.type === 'beaker' ||
      serie.type === 'pyramid' ||
      serie.type === 'funnel'
  }

  /*
   * Determine the offset for a stack
   */
  stackOffset (series) {
    let serie = series[0]
    if (!serie || (serie.type === 'bar' && !serie.offsetExpand)) {
      // Default for stacked bars, which allows bars below 0
      return shape.stackOffsetDiverging
    } else if (serie && zeroOffsetStackables.includes(serie.type)) {
      // Zero baseline
      return shape.stackOffsetNone
    } else {
      // bar with offsetExpand
      return shape.stackOffsetExpand
    }
  }

  /*
   * Groups series by their `stackBy` property, and then stacks those series
   */
  _addStackables (series) {
    nest().key(serie => this._isStackable(serie))
          .entries(series.filter(serie => this._isStackable(serie)))
          .forEach(stack => {
            let rangeIds = stack.values.map(f => f.points.map(p => p.rangeId)) // array of all rangeIds
              , uniqueRangeIds = Array.from(new Set([].concat(...rangeIds)))
              , axis = stack.values[0].axis
              , data = uniqueRangeIds.map(rangeId => {
                let result = {rangeId}
                stack.values.forEach(serie => {
                  // Grab the point at this rangeId
                  result[serie.id] = serie.points.find(point => point.rangeId === rangeId)
                })
                return result
              })
              , stackOffset = this.stackOffset(stack.values)
              , _stack = shape.stack() // Builds a stack. Each item in the array is a serie
                              .keys(stack.values.map(serie => serie.id)) // Key'd by serie id
                              .value((data, key) => {
                                return (data[key] && data[key].raw) || 0
                              })
                              .offset(stackOffset)
                              (data)

            this._stackOffsets[axis || 'left'].add(stackOffset)

            uniqueRangeIds.forEach(rangeId => {
              // Looping over each tick
              // Blocks in a stack for the current range id
              let blocks = _stack.map(stackedSerie => {
                // stacked serie is an array of all the points for the serie.
                // Find the point for this range id
                let point = stackedSerie.find(tick => tick.data.rangeId == rangeId)
                point.serieId = stackedSerie.key
                point.index = stackedSerie.index
                return point
              })

              // For block in this stack
              blocks.forEach(block => {
                block.axis = axis
                let point = block.data[block.serieId] // Grab the actual data point from the serie
                if (point) {
                  point._range = [block[0], block[1]]
                  point._range.axis = axis
                  point._index = block.index // Store the index of the serie w/in the stack

                  this._slots.push(point._range)
                }
              })

              // Now remove series in the stack that don't have a height
              blocks.filter(block => block[1] - block[0]) // Remove blocks missing a height
                .forEach((block, i, all) => {
                  // Tell this point it's the top and/or bottom
                  let point = block.data[block.serieId]
                  point._bottomStack = i === 0
                  point._topStack = i === all.length - 1
                })
            })
          })
  }

  /*
   * Returns a map of axis to columnIdx
   */
  getColumnsByAxis () {
    let map = {left: [], right: []}
    this.plots[0].series.forEach(serie => {
      if (typeof serie._columnIdx !== 'undefined') {
        map[serie.axis || 'left'].push(serie._columnIdx)
      }
    })
    return map
  }

  /*
  * Determines the width of a label as it rendered in 
  * a svg > text element.
  */
  _calculateLabelWidth (label) {
    // This matches the css defined font size for points, where labels attach
    // Ideally it'd be better to use a css3 variable, but we don't access to the `svg` element
    const size = '14px'
    const svg = d3.select('body').append('svg')

    const text = svg.append('text')
      .style('font-size', size)
      .text(label)

    const width = text.node().getBBox().width

    // Remove the placeholder svg
    svg.remove()

    return width
  }

  /*
   * Determines how much space we need on the right side of the chart
   * if the chart is showing name labels.
   */
  _calculateRightMarginOffset (dimensions) {

    // Find the largest `plots[x].series[x].points[x].formatted?.name`
    // in the entire chart
    let label = this.plots.map(plot => plot.series)
      .flat()
      .map(serie => serie.points)
      .flat()
      .map(point => point.formatted?.title || '')
      // Get the longest title text
      .reduce((p, c) =>  c.length > p.length ? c : p, '')

    // Return the minimum of half the width of the chart or the width of the label
    return Math.min(dimensions.width * .5, this._calculateLabelWidth(label))
  }

  /*
   * Builds the map of plot -> columns
   */
  _columnize (dimensions) {
    let hasNameLabels = false // If we have name labels, we need to adjust the right side margin

    this._slots = []

    this.maxColumnsPerGroup = 1 // Max number of columns that will be shown for each item on the x-axis

    this._stackOffsets = {
      left: new Set,
      right: new Set
    }

    this.plots.forEach(plot => {
      let series = plot.series

      this._setColumnIndex(series)

      this.breakdown = breakdown(this.plots[0]) // Assumes all plots have the same range, which we should enforce

      this.hasXAxis = !!this.breakdown.range.length && // Must have some kind of breakdown
                      series.map(serie => serie.type).some(type => hasXAxis.indexOf(type) !== -1) // and showing a serie that displays x-axis

      this.hasYAxis = series.map(serie => serie.type).some(type => hasYAxis.indexOf(type) !== -1)

      // If it's a line/area chart, we need to see if they are showing "name labels". If they are, we need to
      // adjust the right side margin to make room for the potential labels. We know they are showing name labels
      // if any of the points (in any series) contain `formatted?.name`
      hasNameLabels = hasNameLabels ||  series.map(serie => serie.type)
        .some(type => ['line', 'area'].indexOf(type) !== -1) && series.map(serie => serie.points)
        .flat()
        .some(point => point.formatted?.title)

      // Find the largest columnIdx of bar charts
      let maxBarColumnIdx = array.max(series.filter(serie => serie.type === 'bar'), serie => serie._columnIdx)
      this.maxColumnsPerGroup = Math.max(this.maxColumnsPerGroup, maxBarColumnIdx + 1) // index starts at 0

      // For each serie we don't have to stack, add all the points
      this._addUnstackables(plot.series)

      // Add series that need stacking
      this._addStackables(plot.series)

      // Set a _range on each of the forecast points, so our scale functions work
      // similarily to normal points
      plot.series.forEach(serie => {
        if (serie.forecast) {
          serie.forecast.points.forEach(point => {
            point._range = [0, point.raw]
          })
        }
      })
    })

    this.rightMarginOffset = hasNameLabels ? this._calculateRightMarginOffset(dimensions) : 0
  }
}

export default Layout
